import { Link } from "react-router-dom";
import FreshBadge from "@/components/FreshBadge";
import { ArrowRight } from "lucide-react";

/**
 * PageHero - refonte 2026 (inspiration Citwell).
 *
 *  Light hero on cream background, giant black display title, a kicker in
 *  mint, a short lede, pill CTAs, and an optional right-side rectangular
 *  photo. No more dark hero band - the dark bands now live further down
 *  the page via <SectionBand tone="dark">.
 *
 *  Pages that want a dark hero (Radar, Labs) can pass `tone="dark"`.
 */
const PageHero = ({
  kicker,
  title,
  description,
  tone = "cream",       // "cream" | "dark"
  accent = "mint",      // "mint" | "yellow" (controls kicker + corner colour)
  image,                // optional { src, alt, caption }
  visual,               // optional original React illustration
  primaryCta,           // optional { to, label }
  secondaryCta,         // optional { to, label }
  ticker,               // kept for Radar hero compatibility
  compact = false,
  minimal = false,      // one-line title, tight padding (Radar home)
  testId = "page-hero",
}) => {
  const isDark = tone === "dark";
  const bg = isDark ? "bg-[#0E1F1A] text-white" : "bg-[#F6F3EE] text-[#0B1210]";
  const kickerColour = accent === "yellow" ? "text-[#FFD400]" : "text-[#00A878]";
  const leadColour = isDark ? "text-white/70" : "text-[#4B5563]";
  const hasImage = !!(image && image.src);
  const hasMedia = hasImage || !!visual;
  const pad = minimal ? "py-6 sm:py-8" : compact ? "py-10 sm:py-14" : "py-14 sm:py-20";
  const titleSize = minimal
    ? "mt-3 text-3xl sm:text-4xl max-w-none"
    : `mt-5 ${compact ? "text-3xl sm:text-4xl lg:text-5xl" : "text-4xl sm:text-5xl lg:text-6xl"} max-w-4xl`;

  return (
    <section data-testid={testId} className={`${bg} relative overflow-hidden`}>
      <div className={`dl-container ${pad}`}>
        <div className={`grid ${hasMedia ? "lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] gap-12 lg:gap-16 items-center" : ""}`}>
          <div className="min-w-0">
            {kicker && (
              <div className={`font-mono-dl text-[11px] tracking-[0.22em] uppercase ${kickerColour}`}>
                {kicker}
              </div>
            )}
            <h1 className={`font-display ${titleSize}`}>
              {title}
            </h1>
            {description && (
              <p className={`mt-7 text-[17px] sm:text-[19px] leading-[1.55] max-w-2xl ${leadColour}`}>
                {description}
              </p>
            )}
            {(primaryCta || secondaryCta) && (
              <div className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
                {primaryCta && (
                  <Link
                    to={primaryCta.to}
                    data-testid={`${testId}-cta-primary`}
                    className="dl-pill dl-pill-primary"
                  >
                    {primaryCta.label}
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </Link>
                )}
                {secondaryCta && (
                  <Link
                    to={secondaryCta.to}
                    data-testid={`${testId}-cta-secondary`}
                    className={`dl-pill ${isDark ? "dl-pill-outline-light" : "dl-pill-outline"}`}
                  >
                    {secondaryCta.label}
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </Link>
                )}
              </div>
            )}
          </div>

          {visual && <div className="min-w-0" data-testid={`${testId}-visual`}>{visual}</div>}
          {hasImage && (
            <div className="relative aspect-[4/5] lg:aspect-[5/6] rounded-[28px] overflow-hidden bg-[#0E1F1A]" data-testid={`${testId}-image`}>
              <img
                src={image.src}
                alt={image.alt || ""}
                loading="eager"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Subtle green corner mark */}
              <div
                aria-hidden="true"
                className="absolute top-5 left-5 w-12 h-[3px] bg-[#00A878] rounded-full"
              />
              {image.caption && (
                <div className="absolute left-5 bottom-5 right-5">
                  <span className="inline-block font-mono-dl text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 bg-[#0E1F1A]/80 backdrop-blur-sm border border-white/15 text-white/90 rounded-full">
                    {image.caption}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Integrated live ticker - Radar hero legacy support. */}
      {ticker && ticker.items && ticker.items.length > 0 && (
        <div className={`border-t ${isDark ? "border-[#1F2A26]" : "border-[#E6E0D5]"}`}>
          <div className="dl-container relative">
            <div className="py-3 flex items-center gap-4 overflow-x-auto no-scrollbar">
              <span className="shrink-0 inline-flex items-center gap-1.5 font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878] min-h-[40px]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00A878] animate-pulse" />
                {ticker.label}
              </span>
              <div className="flex items-center gap-6">
                {ticker.items.map((n, i) => (
                  <Link
                    key={n.id || i}
                    to={n.to || "#"}
                    data-testid={`hero-ticker-item-${i}`}
                    className="shrink-0 inline-flex items-baseline gap-2 group min-h-[40px]"
                  >
                    <span className="font-mono-dl text-[10px] tracking-wider text-[#6b7280]">
                      {n.published_at}
                    </span>
                    <FreshBadge
                      publishedAt={n.published_at}
                      createdAt={n.created_at}
                      lang={ticker.lang}
                    />
                    <span className={`text-[13px] line-clamp-1 max-w-[380px] transition-colors ${isDark ? "text-white/80 group-hover:text-white" : "text-[#0B1210] group-hover:text-[#00A878]"}`}>
                      {n.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <div
              aria-hidden="true"
              className={`md:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l to-transparent ${isDark ? "from-[#0E1F1A]" : "from-[#F6F3EE]"}`}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default PageHero;
